import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  ModuleRegistry,
  PaginationModule,
  TextFilterModule,
  NumberFilterModule,
  CustomFilterModule,
  TooltipModule,
  CellStyleModule,
  LocaleModule,
  ColumnApiModule,
  CellApiModule,
  RowApiModule,
  EventApiModule,
  GridStateModule,
} from 'ag-grid-community';
import type {
  ColumnVisibleEvent,
  FilterChangedEvent,
  FilterModel,
  GridReadyEvent,
  PaginationChangedEvent,
} from 'ag-grid-community';
import {
  ServerSideRowModelModule,
  ServerSideRowModelApiModule,
  ColumnMenuModule,
  CellSelectionModule,
  StatusBarModule,
  ClipboardModule,
} from 'ag-grid-enterprise';
import { useQueryClient } from '@tanstack/react-query';

import LinearProgress from '@mui/material/LinearProgress';
import Box from '@mui/material/Box';

import AgGridStyles, { ROW_HEIGHT } from '@/components/AgGrid/BankFeedTransactions/AgGridStyles';
import useAgGridProps from '@/components/AgGrid/BankFeedTransactions/useAgGridProps';
import useAgGridColumnTypes from '@/components/AgGrid/BankFeedTransactions/useAgGridColumnTypes';
import AgGridGlobalStyles from '@/components/AgGrid/components/AgGridGlobalStyles';
import useAgGridRtlMethods from '@/components/AgGrid/hooks/useAgGridRtlMethods';
import useAgGridTheme from '@/components/AgGrid/hooks/useAgGridTheme';
import useAgGridTranslations from '@/components/AgGrid/hooks/useAgGridTranslations';

import ActiveFilterChips from '@/pages/company/BankFeedRoute/components/ActiveFilterChips';
import gridComponents from '@/pages/company/BankFeedRoute/components/BankFeedGrid/gridComponents';
import useColumnDefs from '@/pages/company/BankFeedRoute/components/BankFeedGrid/useColumnDefs';
import { getDesignedFlex } from '@/components/AgGrid/BankFeedTransactions/columnWidths';
import { useBankFeedTransactionsGridState } from '@/pages/company/BankFeedRoute/components/BankFeedGrid/useBankFeedTransactionsGridState';
import useServerDatasource from '@/pages/company/BankFeedRoute/components/BankFeedGrid/useServerDatasource';
import NoFilterResults from '@/pages/company/BankFeedRoute/components/EmptyStates/NoFilterResults';
import { useBankFeedRouteContext } from '@/pages/company/BankFeedRoute/context';
import type { SetColumnFilterParams } from '@/pages/company/BankFeedRoute/components/BankFeedGrid/BankFeedSetColumnFilter';
import { bankFeedTransactionsAutocompleteQuery } from '@/domain/bankFeed/queries';
import type { BankFeedAccountType, BankFeedTransactionRow } from '@/domain/bankFeed/schemas';

// AG Grid v34 is modular — every feature a grid uses needs its module registered. The bank-feed
// grid used to register nothing and free-rode on the modules other grids (Workspace / Companies /
// VendorExpenses) happen to register at bootstrap via their static imports in @/router/routes — fragile
// implicit coupling. This list is derived from the grid's actual config (useAgGridProps,
// useAgGridColumnTypes, the colDef filter types in filterHelpers) and its grid-API usage, so it
// covers every code path, not just the ones a manual click-through happens to hit.
ModuleRegistry.registerModules([
  // Row model + paging
  ServerSideRowModelModule, // rowModelType: 'serverSide' + serverSideDatasource
  ServerSideRowModelApiModule, // api.refreshServerSide
  PaginationModule, // pagination
  // Filters — the set/date columns use CUSTOM filter components, so no Set/Date/Multi modules
  TextFilterModule, // agTextColumnFilter + defaultColDef filter: true
  NumberFilterModule, // agNumberColumnFilter
  CustomFilterModule, // bankFeedSetColumnFilter + customDateFilter custom components
  ColumnMenuModule, // columnMenu: 'legacy'
  // Selection sum (DA-19100)
  CellSelectionModule, // cellSelection + api.getCellRanges
  StatusBarModule, // statusBar / bankFeedSumStatusPanel
  ClipboardModule, // copy the drag-selected cells
  // Display
  TooltipModule, // tooltipShowMode + bank column tooltipValueGetter
  CellStyleModule, // CENTERED_CELL_STYLE / cellClass on column types
  LocaleModule, // getLocaleText
  // Grid-API surface used by the page
  ColumnApiModule, // getColumnState / applyColumnState (buildGridSnapshot)
  CellApiModule, // api.getCellValue (selection-sum)
  RowApiModule, // api.getDisplayedRowAtIndex (selection-sum)
  EventApiModule, // api.addEventListener('cellSelectionChanged')
  GridStateModule, // initialState + onStateUpdated (per-user column layout persistence)
]);

// Wired into AG Grid via `context={...}` and consumed by custom components
// (filters, renderers) that need grid-wide selection state.
export type BankFeedGridContext = {
  accountType: BankFeedAccountType;
  currency: string;
  accountIds: string[];
};

const BankFeedGrid: React.FC = () => {
  const { accountType, toolbarFilters, gridApiRef } = useBankFeedRouteContext();
  const queryClient = useQueryClient();

  // Stable loader injected into every set-filter column via `filterParams`. Reads the
  // current accountType/currency fresh from the grid `context` at call time (so a
  // kept-mounted filter is never stale) and fetches through the TanStack cache. The
  // query is defined with `staleTime: 0`, so each open refetches fresh options (the BE
  // derives them from current grid transactions) while concurrent same-key opens still
  // dedupe. Stable identity (deps: [queryClient]) keeps `columnDefs` from changing on a
  // currency switch (no per-column refetch storm).
  const loadOptions = useCallback<SetColumnFilterParams['loadOptions']>(
    ({ field, api }) => {
      const { accountType: currentAccountType, currency, accountIds } = api.getGridOption(
        'context',
      ) as BankFeedGridContext;
      return queryClient
        .fetchQuery(
          bankFeedTransactionsAutocompleteQuery({ field, accountType: currentAccountType, currency, accountIds }),
        )
        .then((data) => data.options);
    },
    [queryClient],
  );

  const { columnDefs, isFetched } = useColumnDefs({ accountType, loadOptions });
  const { initialState, onStateUpdated } = useBankFeedTransactionsGridState({ tabKey: accountType, columnDefs });
  const { datasource: serverSideDatasource, resetCursors } = useServerDatasource({
    accountType,
    toolbarFilters,
  });
  const baseProps = useAgGridProps();
  const columnTypes = useAgGridColumnTypes();
  const { enableRtl } = useAgGridRtlMethods();
  const agGridTheme = useAgGridTheme();
  const { getLocaleText } = useAgGridTranslations();

  const [filterModel, setFilterModel] = useState<FilterModel>({});

  const handleGridReady = useCallback(
    (event: GridReadyEvent<BankFeedTransactionRow>) => {
      gridApiRef.current = event.api;
    },
    [gridApiRef],
  );

  // Clear the upstream ref on unmount so the export button can't fire against
  // an api from a different tab / account-type mount.
  useEffect(
    () => () => {
      gridApiRef.current = null;
    },
    [gridApiRef],
  );

  // DA-19485: on the CARD tab the grid needs at least one seeded payment day before it can build a
  // valid search payload (a non-empty `dates` set). Until then withhold the datasource (see the
  // AgGridReact prop below) — AG Grid shows its loading rows and fires no getRows, while the grid
  // stays mounted so its applied column filters / sort survive. Bank tab is always ready.
  const isCardDatasourceReady = accountType !== 'CARD' || toolbarFilters.paymentDates.length > 0;
  const wasDatasourceReadyRef = useRef(isCardDatasourceReady);

  // Filter / sort change → cursor map invalid for new query → reset to page 0 and refetch. When the
  // CARD datasource transitions absent → present (a payment date just seeded), attaching it already
  // triggers a fresh load, so skip the refresh to avoid a double getRows.
  useEffect(() => {
    const api = gridApiRef.current;
    const becameReady = isCardDatasourceReady && !wasDatasourceReadyRef.current;
    wasDatasourceReadyRef.current = isCardDatasourceReady;
    if (api == null) return;
    if (becameReady) return;
    resetCursors();
    api.paginationGoToFirstPage();
    api.refreshServerSide({ purge: true });
  }, [toolbarFilters, isCardDatasourceReady, resetCursors, gridApiRef]);

  const handleSortChanged = useCallback(() => {
    const api = gridApiRef.current;
    if (api == null) return;
    resetCursors();
    api.paginationGoToFirstPage();
  }, [resetCursors, gridApiRef]);

  // Column filter change → mirror to local state for chip rendering, reset cursors and
  // jump to first page (in parity with handleSortChanged). AG Grid auto-refetches SSRM
  // on filter change, so no explicit refreshServerSide is needed here.
  const handleFilterChanged = useCallback(
    (event: FilterChangedEvent<BankFeedTransactionRow>) => {
      setFilterModel(event.api.getFilterModel() ?? {});
      resetCursors();
      event.api.paginationGoToFirstPage();
    },
    [resetCursors],
  );

  // Resolve a colId to its column header for chip labels. Reads gridApi at call-time
  // so the callback identity stays stable across renders.
  const getColumnHeader = useCallback(
    (colId: string) => {
      const headerName = gridApiRef.current?.getColumn(colId)?.getColDef().headerName;
      return typeof headerName === 'string' && headerName !== '' ? headerName : colId;
    },
    [gridApiRef],
  );

  // Remove one column's filter. AG Grid fires onFilterChanged → local state syncs.
  const handleChipDelete = useCallback(
    (colId: string) => {
      const api = gridApiRef.current;
      if (api == null) return;
      const { [colId]: _removed, ...next } = api.getFilterModel() ?? {};
      api.setFilterModel(Object.keys(next).length > 0 ? next : null);
    },
    [gridApiRef],
  );

  // Clear all column filters. Toolbar filters MUST NOT be affected.
  const handleReset = useCallback(() => {
    gridApiRef.current?.setFilterModel(null);
  }, [gridApiRef]);

  // Cursor pagination invariant: `cacheBlockSize` must equal `paginationPageSize`. If a user
  // picks a different size, AG Grid keeps the original block size — the visible page would
  // split across 2 / 4 BE calls (each carrying stale cursors). Re-pin cacheBlockSize, drop the
  // cursor map (old tokens tied to old block boundaries), reset to page 0, purge.
  const handlePaginationChanged = useCallback(
    (event: PaginationChangedEvent<BankFeedTransactionRow>) => {
      if (event.newPageSize !== true) return;
      const newSize = event.api.paginationGetPageSize();
      event.api.setGridOption('cacheBlockSize', newSize);
      resetCursors();
      event.api.paginationGoToFirstPage();
      event.api.refreshServerSide({ purge: true });
    },
    [resetCursors],
  );

  // AG Grid nulls a column's `flex` when it is manually resized (`Column.setActualWidth`), turning
  // it fixed-width; hiding a column then leaves empty space instead of the remaining columns
  // stretching (DA-19187). Re-apply each column's designed flex on a user-driven show/hide so the
  // grid fills again. Guarded to `source === 'columnMenu'` (the Edit-icon columns menu) so restoring
  // persisted layout on load (`source === 'gridInitializing'`) keeps the user's saved widths. Every
  // column is listed in the state — `applyColumnState` clears flex on any column omitted from it.
  const handleColumnVisible = useCallback((event: ColumnVisibleEvent<BankFeedTransactionRow>) => {
    if (event.source !== 'columnMenu') return;
    const state = event.api.getColumnState().map((col) => {
      const flex = getDesignedFlex(col.colId);
      // Fixed columns (debit / credit, service): clear the stray type-level flex explicitly so they
      // never flex when unpinned — passing width alone would leave that lingering flex intact.
      return flex != null ? { colId: col.colId, flex } : { colId: col.colId, flex: null, width: col.width };
    });
    event.api.applyColumnState({ state });
  }, []);

  // `currency!` is type-level shorthand for a runtime invariant enforced one
  // level up: BankFeedRoute gates the Outlet on `isGridReady = toolbarFilters
  // .currency != null`, so this component only mounts after a currency is
  // selected. If that gate ever moves or weakens, this assert needs to move
  // with it (consumers downstream read `currency: string` from context without
  // null checks). Memoized so the context reference stays stable.
  const gridContext = useMemo<BankFeedGridContext>(
    () => ({ accountType, currency: toolbarFilters.currency!, accountIds: toolbarFilters.accountIds }),
    [accountType, toolbarFilters.currency, toolbarFilters.accountIds],
  );

  if (!isFetched) {
    return <LinearProgress />;
  }

  return (
    <>
      <ActiveFilterChips
        filterModel={filterModel}
        getColumnHeader={getColumnHeader}
        onChipDelete={handleChipDelete}
        onReset={handleReset}
      />
      <Box
        className="ag-theme-dokka ag-theme-dokka--bank-feed-transactions"
        sx={{ flex: 1, minHeight: 0, width: '100%' }}
      >
        <AgGridGlobalStyles />
        <AgGridStyles />
        <AgGridReact<BankFeedTransactionRow>
          {...baseProps}
          theme={agGridTheme}
          enableRtl={enableRtl}
          rowHeight={ROW_HEIGHT}
          columnDefs={columnDefs}
          columnTypes={columnTypes}
          initialState={initialState}
          onStateUpdated={onStateUpdated}
          serverSideDatasource={isCardDatasourceReady ? serverSideDatasource : undefined}
          components={gridComponents}
          context={gridContext}
          getLocaleText={getLocaleText}
          onGridReady={handleGridReady}
          onSortChanged={handleSortChanged}
          onFilterChanged={handleFilterChanged}
          onColumnVisible={handleColumnVisible}
          onPaginationChanged={handlePaginationChanged}
          noRowsOverlayComponent={NoFilterResults}
        />
      </Box>
    </>
  );
};

export default BankFeedGrid;
